import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Sample, Test, Assay, Order, ListOptions, QueryFilter, SearchColumn } from '@mariana/labos-client';
import type { EntityType, PaginationInfo } from '../types';
import { limsClient } from '../api/limsClient';
import { resolveSourceColumns } from '../utils/resolveColumns';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';

export type { PaginationInfo };

type AnyEntity = Sample | Test | Assay | Order;

interface UseEntityListOptions {
  entityType: EntityType;
  columns: ColumnDef[];
  /** Filters seeded on first load when URL has no filter params. Replaces the old read-only presetFilters. */
  initialFilters?: QueryFilter[];
  /** Additional filters managed outside the filter builder (e.g. date range). */
  extraFilters?: QueryFilter[];
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  /** A stable ID to reset state when the "view" changes. Defaults to entityType. */
  viewId?: string;
  /** When true, skip fetching (e.g. when showing detail view). */
  disabled?: boolean;
  /** Server-side search string. Space-separated tokens, each must match some text attribute. */
  search?: string;
}

export interface UseEntityListResult {
  entities: AnyEntity[];
  resolvedEntities: ResolvedEntityMap;
  pagination: PaginationInfo;
  filters: QueryFilter[];
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc';
  loading: boolean;
  error: string | null;
  handleFiltersChange: (filters: QueryFilter[]) => void;
  handlePageChange: (offset: number) => void;
  handleLimitChange: (limit: number) => void;
  handleSortChange: (column: string) => void;
  refreshEntities: () => void;
  fetchAllEntities: (onProgress: (fetched: number, total: number) => void) => Promise<AnyEntity[]>;
}

/** Decode filters param; tolerate accidental double-encoding in shared links. */
function parseFiltersSearchParam(raw: string): QueryFilter[] {
  let s = raw.trim();
  for (let i = 0; i < 5; i++) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed as QueryFilter[];
      break;
    } catch {
      try {
        const next = decodeURIComponent(s);
        if (next === s) break;
        s = next;
      } catch {
        break;
      }
    }
  }
  return [];
}

function deriveSearchColumns(columns: ColumnDef[]): SearchColumn[] {
  const cols: SearchColumn[] = [];
  for (const col of columns) {
    if (col.extract) continue;
    if (col.render === 'date') continue;
    if (col.key === 'id') continue;
    cols.push({ attribute: col.key });
  }
  return cols;
}

export function useEntityList({
  entityType,
  columns,
  initialFilters = [],
  extraFilters = [],
  defaultSort,
  viewId,
  disabled = false,
  search,
}: UseEntityListOptions): UseEntityListResult {
  const searchCols = useMemo(() => deriveSearchColumns(columns), [columns]);
  const effectiveViewId = viewId ?? entityType;
  const defaultSortBy = defaultSort?.by ?? 'id';
  const defaultSortOrder = defaultSort?.order ?? 'desc';

  const [searchParams, setSearchParams] = useSearchParams();
  const [entities, setEntities] = useState<AnyEntity[]>([]);
  const [resolvedEntities, setResolvedEntities] = useState<ResolvedEntityMap>(new Map());
  const [pagination, setPagination] = useState<PaginationInfo>({ limit: 25, offset: 0, count: 0 });
  const [filters, setFilters] = useState<QueryFilter[]>([]);
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const parseURLParams = useCallback(() => {
    const limitParam = searchParams.get('limit');
    const offsetParam = searchParams.get('offset');
    const limit = limitParam ? parseInt(limitParam, 10) : 25;
    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    const sb = searchParams.get('sort_by') || defaultSortBy;
    const soParam = searchParams.get('sort_order') as 'asc' | 'desc' | null;
    const so = soParam === 'asc' || soParam === 'desc' ? soParam : defaultSortOrder;

    let urlFilters: QueryFilter[] = [];
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      urlFilters = parseFiltersSearchParam(filtersParam);
    }

    return {
      limit: !isNaN(limit) && limit > 0 && limit <= 1000 ? limit : 25,
      offset: !isNaN(offset) && offset >= 0 ? offset : 0,
      sortBy: sb,
      sortOrder: so,
      filters: urlFilters,
    };
  }, [searchParams, defaultSortBy, defaultSortOrder]);

  useEffect(() => {
    const { limit, offset, sortBy: sb, sortOrder: so, filters: uf } = parseURLParams();
    setPagination(prev => ({ ...prev, limit, offset }));
    setSortBy(sb);
    setSortOrder(so);
    setFilters(uf.length > 0 ? uf : initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewId, parseURLParams]);

  const writeURL = useCallback((
    newLimit: number,
    newOffset: number,
    newSortBy?: string,
    newSortOrder: 'asc' | 'desc' = defaultSortOrder,
    newFilters: QueryFilter[] = filters,
  ) => {
    if (disabled) return;
    // Use functional updates so `cols` is read from the latest URL when this runs
    // in the same React batch as setVisibleColumns (stale closure would drop cols).
    setSearchParams(prev => {
      const params = new URLSearchParams();
      if (newLimit !== 25) params.set('limit', newLimit.toString());
      if (newOffset !== 0) params.set('offset', newOffset.toString());
      if (newSortBy) {
        params.set('sort_by', newSortBy);
        params.set('sort_order', newSortOrder);
      }
      if (newFilters.length > 0) {
        params.set('filters', JSON.stringify(newFilters));
      }
      const cols = prev.get('cols');
      if (cols) params.set('cols', cols);
      if (params.toString() === prev.toString()) return prev;
      return params;
    }, { replace: true });
  }, [setSearchParams, filters, disabled, defaultSortOrder]);

  const fetchKey = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete('cols');
    return p.toString();
  }, [searchParams]);

  useEffect(() => {
    if (disabled) return;

    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { limit, offset, sortBy: sb, sortOrder: so, filters: uf } = parseURLParams();
        const allFilters = uf.length > 0 ? uf : initialFilters;
        const mergedFilters = [...allFilters, ...extraFilters];

        const options: ListOptions = {
          filters: mergedFilters,
          limit,
          offset,
          ...(sb && { sort_by: sb, sort_order: so }),
          exclude_attributes: ['raw_worksheet_data'],
          ...(search && { search, search_columns: searchCols }),
        };

        const response = await limsClient.listEntitiesByType(entityType, options);
        if (controller.signal.aborted) return;

        let resolved: ResolvedEntityMap = new Map();
        try {
          resolved = await resolveSourceColumns(response.data, columns, entityType);
        } catch (resolveErr) {
          console.warn('Failed to resolve cross-entity columns:', resolveErr);
        }
        if (controller.signal.aborted) return;

        setEntities(response.data);
        setResolvedEntities(resolved);
        setPagination({
          limit: response.pagination.limit,
          offset: response.pagination.offset,
          count: response.pagination.count,
        });
        setFilters(allFilters);
        setSortBy(sb);
        setSortOrder(so);
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load entities';
        setError(msg);
        console.error('Failed to load entities:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewId, fetchKey, disabled, JSON.stringify(extraFilters), search, refreshKey]);

  const handleFiltersChange = useCallback((newFilters: QueryFilter[]) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
    writeURL(pagination.limit, 0, sortBy, sortOrder, newFilters);
  }, [pagination.limit, sortBy, sortOrder, writeURL]);

  const handlePageChange = useCallback((newOffset: number) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
    writeURL(pagination.limit, newOffset, sortBy, sortOrder);
  }, [pagination.limit, sortBy, sortOrder, writeURL]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
    writeURL(newLimit, 0, sortBy, sortOrder);
  }, [sortBy, sortOrder, writeURL]);

  const handleSortChange = useCallback((col: string) => {
    const newOrder = sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(col);
    setSortOrder(newOrder);
    setPagination(prev => ({ ...prev, offset: 0 }));
    writeURL(pagination.limit, 0, col, newOrder);
  }, [sortBy, sortOrder, pagination.limit, writeURL]);

  const refreshEntities = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const fetchAllEntities = useCallback(async (
    onProgress: (fetched: number, total: number) => void,
  ): Promise<AnyEntity[]> => {
    const { sortBy: sb, sortOrder: so, filters: uf } = parseURLParams();
    const allFilters = uf.length > 0 ? uf : initialFilters;
    const mergedFilters = [...allFilters, ...extraFilters];
    const total = pagination.count;

    const allEntities: AnyEntity[] = [];
    let offset = 0;
    const batchSize = 500;

    while (offset < total) {
      const options: ListOptions = {
        filters: mergedFilters,
        limit: batchSize,
        offset,
        ...(sb && { sort_by: sb, sort_order: so }),
        exclude_attributes: ['raw_worksheet_data'],
        ...(search && { search, search_columns: searchCols }),
      };
      const response = await limsClient.listEntitiesByType(entityType, options);
      allEntities.push(...response.data);
      offset += batchSize;
      onProgress(allEntities.length, total);
    }

    return allEntities;
  }, [entityType, parseURLParams, initialFilters, extraFilters, pagination.count, search, searchCols]);

  return {
    entities,
    resolvedEntities,
    pagination,
    filters,
    sortBy,
    sortOrder,
    loading,
    error,
    handleFiltersChange,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    refreshEntities,
    fetchAllEntities,
  };
}
